/**
 * CanvasPageStage
 * The center editing canvas. Sections are invisible containers — no header chrome.
 * Elements render as pure content. Selection ring, floating toolbar, and image resize
 * handles are layered on top. GHL / ClickFunnels style.
 */
import { type CSSProperties, useEffect, useRef, useState } from "react";
import {
  AlignCenter,
  AlignLeft,
  AlignRight,
  Check,
  Columns2,
  Columns3,
  FileText,
  GripVertical,
  ImageIcon,
  LayoutGrid,
  LayoutPanelTop,
  Loader2,
  PanelLeft,
  PanelRight,
  Pencil,
  Plus,
  Rows3,
  Trash2,
  Type,
  PenSquare,
  Minus,
  SquareDashedBottom,
  Badge,
  Building2,
  ClipboardList,
  DollarSign,
  FileSignature,
  Upload,
} from "lucide-react";
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
  rectSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { cn } from "@/lib/utils";
import type {
  ProposalDocumentLayoutPage,
  TemplatePageElementType,
  TemplatePageLayoutMode,
  TemplatePageSectionType,
} from "../../types";
import { BuilderElementPreview, resolveText, type BrandPreviewData, type LineItemUpdate } from "./BuilderElementPreview";
import type { BuilderElementDraft } from "./BuilderSectionElementList";
import { useBuilderImageUpload } from "../../hooks/useBuilderImageUpload";
import { useBrandSettings, useBrandAssets, useBrandFonts } from "../../hooks/useBrandSettings";
import { resolveBrandTypography, buildFontFaceCSS } from "../../utils/brandTypography";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type CanvasSelection =
  | null
  | { kind: "section"; sectionId: string }
  | { kind: "element"; sectionId: string; elementId: string };

type CanvasSectionDraft = {
  id: string;
  title: string;
  type: TemplatePageSectionType;
  layoutMode: TemplatePageLayoutMode;
  settings: Record<string, string | boolean | number | null>;
  elements: BuilderElementDraft[];
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function hexToRgba(hex: string, opacity: number): string {
  const h = hex.replace("#", "");
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  if (Number.isNaN(r) || Number.isNaN(g) || Number.isNaN(b)) return hex;
  return `rgba(${r},${g},${b},${Math.max(0, Math.min(1, opacity))})`;
}

function normalizeSections(page: ProposalDocumentLayoutPage): CanvasSectionDraft[] {
  if (!Array.isArray(page.sections)) return [];

  return page.sections.map((section, index) => {
    const rec =
      section && typeof section === "object" && !Array.isArray(section)
        ? (section as Record<string, unknown>)
        : {};

    const rawType   = rec.type;
    const rawLayout = rec.layoutMode;

    const type: TemplatePageSectionType =
      rawType === "hero" || rawType === "content" || rawType === "media" ||
      rawType === "services" || rawType === "summary" || rawType === "footer" ||
      rawType === "custom"
        ? rawType : "content";

    const layoutMode: TemplatePageLayoutMode =
      rawLayout === "stack" || rawLayout === "two_column" || rawLayout === "three_column" ||
      rawLayout === "split_left" || rawLayout === "split_right" || rawLayout === "grid" ||
      rawLayout === "custom"
        ? rawLayout : "stack";

    return {
      id:         typeof rec.id === "string" && rec.id.length > 0 ? rec.id : `section_fallback_${index}`,
      title:      typeof rec.title === "string" && rec.title.trim().length > 0 ? rec.title : "Untitled Section",
      type,
      layoutMode,
      settings:   rec.settings && typeof rec.settings === "object" && !Array.isArray(rec.settings)
        ? (rec.settings as Record<string, string | boolean | number | null>) : {},
      elements:   Array.isArray(rec.elements) ? (rec.elements as BuilderElementDraft[]) : [],
    };
  });
}

function sectionTypeAccentClass(type: string) {
  switch (type) {
    case "hero":     return "border-sky-400/30 bg-sky-400/10 text-sky-300";
    case "content":  return "border-slate-400/30 bg-slate-400/10 text-slate-300";
    case "media":    return "border-violet-400/30 bg-violet-400/10 text-violet-300";
    case "services": return "border-emerald-400/30 bg-emerald-400/10 text-emerald-300";
    case "summary":  return "border-amber-400/30 bg-amber-400/10 text-amber-300";
    case "footer":   return "border-pink-400/30 bg-pink-400/10 text-pink-300";
    default:         return "border-white/15 bg-white/[0.06] text-white/50";
  }
}

function getGridClass(layoutMode: TemplatePageLayoutMode) {
  switch (layoutMode) {
    case "two_column":   return "grid-cols-1 md:grid-cols-2";
    case "three_column": return "grid-cols-1 md:grid-cols-3";
    case "split_left":   return "grid-cols-1 md:grid-cols-[1.35fr_0.65fr]";
    case "split_right":  return "grid-cols-1 md:grid-cols-[0.65fr_1.35fr]";
    case "grid":         return "grid-cols-1 md:grid-cols-2";
    default:             return "grid-cols-1";
  }
}

function layoutModeIcon(mode: TemplatePageLayoutMode) {
  switch (mode) {
    case "two_column":   return <Columns2 className="h-3 w-3" />;
    case "three_column": return <Columns3 className="h-3 w-3" />;
    case "split_left":   return <PanelLeft className="h-3 w-3" />;
    case "split_right":  return <PanelRight className="h-3 w-3" />;
    case "grid":         return <LayoutGrid className="h-3 w-3" />;
    default:             return <LayoutPanelTop className="h-3 w-3" />;
  }
}

function elementTypeIcon(type: TemplatePageElementType) {
  switch (type) {
    case "text":            return <Type className="h-3.5 w-3.5" />;
    case "rich_text":       return <PenSquare className="h-3.5 w-3.5" />;
    case "image":
    case "logo":            return <ImageIcon className="h-3.5 w-3.5" />;
    case "divider":         return <Minus className="h-3.5 w-3.5" />;
    case "spacer":          return <SquareDashedBottom className="h-3.5 w-3.5" />;
    case "badge":           return <Badge className="h-3.5 w-3.5" />;
    case "company_info":    return <Building2 className="h-3.5 w-3.5" />;
    case "project_summary":
    case "service_table":   return <ClipboardList className="h-3.5 w-3.5" />;
    case "pricing_summary": return <DollarSign className="h-3.5 w-3.5" />;
    case "signature_block": return <FileSignature className="h-3.5 w-3.5" />;
    default:                return <Type className="h-3.5 w-3.5" />;
  }
}

function isMediaElement(type: TemplatePageElementType) {
  return type === "image" || type === "logo";
}

function isInlineEditable(type: TemplatePageElementType) {
  return [
    "text", "rich_text", "badge", "spacer", "image", "logo",
    "static_content", "custom",
  ].includes(type);
}

function alignFlexClass(alignment?: unknown): string {
  switch (alignment) {
    case "center": return "justify-center";
    case "right":  return "justify-end";
    default:       return "justify-start";
  }
}

const ALIGN_OPTIONS = [
  { value: "left",   Icon: AlignLeft,   title: "Align left" },
  { value: "center", Icon: AlignCenter, title: "Align center" },
  { value: "right",  Icon: AlignRight,  title: "Align right" },
] as const;

// ---------------------------------------------------------------------------
// Image resize handle
// ---------------------------------------------------------------------------

const HANDLE_POSITIONS = ["nw", "n", "ne", "e", "se", "s", "sw", "w"] as const;
type HandlePosition = (typeof HANDLE_POSITIONS)[number];

function handlePositionClass(pos: HandlePosition) {
  switch (pos) {
    case "nw": return "-top-1.5 -left-1.5 cursor-nw-resize";
    case "n":  return "-top-1.5 left-1/2 -translate-x-1/2 cursor-n-resize";
    case "ne": return "-top-1.5 -right-1.5 cursor-ne-resize";
    case "e":  return "top-1/2 -right-1.5 -translate-y-1/2 cursor-e-resize";
    case "se": return "-bottom-1.5 -right-1.5 cursor-se-resize";
    case "s":  return "-bottom-1.5 left-1/2 -translate-x-1/2 cursor-s-resize";
    case "sw": return "-bottom-1.5 -left-1.5 cursor-sw-resize";
    case "w":  return "top-1/2 -left-1.5 -translate-y-1/2 cursor-w-resize";
  }
}

// ---------------------------------------------------------------------------
// Inline element editors
// ---------------------------------------------------------------------------

type InlineEditorProps = {
  element: BuilderElementDraft;
  onUpdate: (updates: Partial<BuilderElementDraft>) => void;
  onDone: () => void;
  brand?: BrandPreviewData;
};

/** Builds the same text style used by BuilderElementPreview */
function buildInlineTextStyle(element: BuilderElementDraft): CSSProperties {
  const fw = (element.style?.fontWeight as string | undefined) ?? "normal";
  return {
    fontSize:   (element.style?.fontSize as number | undefined) ?? 16,
    fontWeight: fw === "bold" ? 700 : fw === "semibold" ? 600 : fw === "medium" ? 500 : 400,
    color:      (element.style?.color as string | undefined) || "#111827",
    textAlign:  ((element.style?.textAlign as string | undefined) ?? "left") as CSSProperties["textAlign"],
    lineHeight: 1.7,
    minHeight:  "1em",
    whiteSpace: "pre-wrap",
    outline:    "none",
    caretColor: "#3b82f6",
  };
}

/** contenteditable text — shows resolved text so what you see is what you edit */
function InlineTextEdit({ element, onUpdate, onDone, brand }: InlineEditorProps) {
  const ref        = useRef<HTMLDivElement>(null);
  const savedRef   = useRef(false);
  const rawTextRef = useRef((element.content?.text as string | undefined) ?? "");
  const resolvedRef = useRef(resolveText(rawTextRef.current, brand));

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    // Show resolved text so user sees the real value (e.g. "Proposal for John" not "[Client Name]")
    el.textContent = resolvedRef.current;
    el.focus();
    try {
      const range = document.createRange();
      const sel   = window.getSelection();
      range.selectNodeContents(el);
      range.collapse(false);
      sel?.removeAllRanges();
      sel?.addRange(range);
    } catch (_) {}
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function save(el: HTMLDivElement) {
    if (savedRef.current) return;
    savedRef.current = true;
    const edited = el.textContent ?? "";
    // If the user didn't change anything, keep the original raw template text (preserves tokens)
    onUpdate({ content: { text: edited === resolvedRef.current ? rawTextRef.current : edited } });
    onDone();
  }

  return (
    <div
      ref={ref}
      contentEditable
      suppressContentEditableWarning
      style={buildInlineTextStyle(element)}
      className="rounded-sm ring-2 ring-blue-400/50 ring-offset-1"
      onBlur={(e) => save(e.currentTarget)}
      onKeyDown={(e) => {
        if (e.key === "Escape") { e.preventDefault(); save(e.currentTarget as HTMLDivElement); }
      }}
    />
  );
}

/** textarea-based rich text (HTML) editor */
function InlineRichTextEdit({ element, onUpdate, onDone }: InlineEditorProps) {
  const [html, setHtml] = useState((element.content?.html as string | undefined) ?? "<p></p>");

  function save() { onUpdate({ content: { html } }); onDone(); }

  return (
    <div className="space-y-2">
      <textarea
        autoFocus
        value={html}
        onChange={(e) => setHtml(e.target.value)}
        onKeyDown={(e) => { if (e.key === "Escape") save(); }}
        rows={6}
        placeholder="Enter HTML content…"
        className="w-full rounded-xl border border-blue-400/30 bg-white px-3 py-2 font-mono text-xs text-gray-700 shadow-inner placeholder:text-gray-400 focus:border-blue-400 focus:outline-none focus:ring-1 focus:ring-blue-400/30"
      />
      <div className="flex gap-2">
        <button
          type="button"
          onClick={save}
          className="flex items-center gap-1.5 rounded-lg bg-blue-500 px-3 py-1.5 text-xs font-semibold text-white hover:bg-blue-600"
        >
          <Check className="h-3 w-3" /> Done
        </button>
        <button
          type="button"
          onClick={onDone}
          className="rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-medium text-gray-500 hover:border-gray-300"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

/** Badge text inline */
function InlineBadgeEdit({ element, onUpdate, onDone }: InlineEditorProps) {
  const [val, setVal] = useState((element.content?.text as string | undefined) ?? "Badge");

  function save() { onUpdate({ content: { text: val } }); onDone(); }

  return (
    <div className="inline-flex items-center gap-2">
      <input
        autoFocus
        value={val}
        onChange={(e) => setVal(e.target.value)}
        onBlur={save}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === "Escape") save(); }}
        className="rounded-full border-2 border-blue-400/40 px-4 py-1.5 text-xs font-semibold text-gray-800 focus:border-blue-400 focus:outline-none"
      />
    </div>
  );
}

/** Spacer height stepper */
function InlineSpacerEdit({ element, onUpdate, onDone }: InlineEditorProps) {
  const [h, setH] = useState((element.content?.height as number | undefined) ?? 24);

  function save() { onUpdate({ content: { height: h } }); onDone(); }

  return (
    <div
      className="flex min-h-[48px] items-center justify-center gap-3 rounded-xl border-2 border-dashed border-blue-400/40 bg-blue-50/60 py-3"
      style={{ height: Math.max(48, h) }}
    >
      <button
        type="button"
        onClick={() => setH((v) => Math.max(8, v - 8))}
        className="flex h-7 w-7 items-center justify-center rounded-full border border-gray-200 bg-white text-sm font-bold text-gray-600 hover:border-gray-300"
      >−</button>
      <span className="min-w-[52px] text-center text-sm font-semibold text-gray-700">{h}px</span>
      <button
        type="button"
        onClick={() => setH((v) => v + 8)}
        className="flex h-7 w-7 items-center justify-center rounded-full border border-gray-200 bg-white text-sm font-bold text-gray-600 hover:border-gray-300"
      >+</button>
      <button
        type="button"
        onClick={save}
        className="flex items-center gap-1 rounded-lg bg-blue-500 px-3 py-1 text-xs font-semibold text-white hover:bg-blue-600"
      >
        <Check className="h-3 w-3" /> Done
      </button>
    </div>
  );
}

/** Image / logo: URL + upload + pixel size inputs */
function InlineImageEdit({ element, onUpdate, onDone }: InlineEditorProps) {
  const [url, setUrl]     = useState((element.content?.src as string | undefined) ?? "");
  const [width, setWidth] = useState<string>(
    element.style?.width ? String(element.style.width) : "",
  );
  const [height, setHeight] = useState<string>(
    element.style?.imageHeight ? String(element.style.imageHeight) : "",
  );
  const [error, setError] = useState("");
  const [linked, setLinked] = useState(true); // lock aspect ratio
  const fileRef = useRef<HTMLInputElement>(null);
  const { mutate: upload, isPending } = useBuilderImageUpload();

  function applySize(w: string, h: string) {
    const wNum = parseInt(w, 10);
    const hNum = parseInt(h, 10);
    onUpdate({
      style: {
        width:       wNum > 0 ? wNum : null,
        imageHeight: hNum > 0 ? hNum : null,
      },
    });
  }

  function handleWidthChange(raw: string) {
    const digits = raw.replace(/\D/g, "");
    setWidth(digits);
    if (linked && digits && height) {
      // keep height proportional if both are set — user can unlink to set freely
    }
    applySize(digits, height);
  }

  function handleHeightChange(raw: string) {
    const digits = raw.replace(/\D/g, "");
    setHeight(digits);
    applySize(width, digits);
  }

  function handleFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setError("");
    upload(file, {
      onSuccess: ({ publicUrl }) => {
        setUrl(publicUrl);
        onUpdate({ content: { src: publicUrl } });
        onDone();
      },
      onError: (err: unknown) => {
        setError((err as { message?: string }).message ?? "Upload failed");
      },
    });
  }

  function save() {
    onUpdate({ content: { src: url } });
    applySize(width, height);
    onDone();
  }

  return (
    <div className="space-y-3">
      {/* Preview */}
      <BuilderElementPreview element={element} />

      {/* Source row */}
      <div className="flex gap-2">
        <input
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") save(); if (e.key === "Escape") onDone(); }}
          placeholder="Paste image URL…"
          className="flex-1 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700 placeholder:text-gray-400 focus:border-blue-400 focus:outline-none focus:ring-1 focus:ring-blue-400/30"
        />
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          disabled={isPending}
          className="flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-2 text-xs font-medium text-gray-600 hover:border-gray-300 disabled:opacity-50"
        >
          {isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
          Upload
        </button>
      </div>

      {/* Size row */}
      <div className="flex items-center gap-2">
        <p className="shrink-0 text-[11px] font-semibold uppercase tracking-[0.1em] text-gray-400">
          Size
        </p>
        {/* Width */}
        <div className="relative flex-1">
          <input
            value={width}
            onChange={(e) => handleWidthChange(e.target.value)}
            placeholder="Auto"
            maxLength={5}
            className="w-full rounded-lg border border-gray-200 bg-white py-2 pl-3 pr-8 text-sm text-gray-800 placeholder:text-gray-400 focus:border-blue-400 focus:outline-none focus:ring-1 focus:ring-blue-400/30"
          />
          <span className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-[11px] text-gray-400">px</span>
        </div>

        {/* Link / unlink button */}
        <button
          type="button"
          onClick={() => setLinked((v) => !v)}
          title={linked ? "Unlink width & height" : "Link width & height"}
          className={cn(
            "flex h-7 w-7 shrink-0 items-center justify-center rounded-md border text-[11px] transition",
            linked
              ? "border-blue-300 bg-blue-50 text-blue-500"
              : "border-gray-200 bg-white text-gray-400 hover:border-gray-300",
          )}
        >
          {linked ? "🔗" : "⛓️"}
        </button>

        {/* Height */}
        <div className="relative flex-1">
          <input
            value={height}
            onChange={(e) => handleHeightChange(e.target.value)}
            placeholder="Auto"
            maxLength={5}
            className="w-full rounded-lg border border-gray-200 bg-white py-2 pl-3 pr-8 text-sm text-gray-800 placeholder:text-gray-400 focus:border-blue-400 focus:outline-none focus:ring-1 focus:ring-blue-400/30"
          />
          <span className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-[11px] text-gray-400">px</span>
        </div>

        {/* Reset to auto */}
        {(width || height) && (
          <button
            type="button"
            onClick={() => { setWidth(""); setHeight(""); applySize("", ""); }}
            className="shrink-0 rounded-md border border-gray-200 bg-white px-2 py-2 text-[11px] text-gray-400 hover:border-gray-300 hover:text-gray-600"
            title="Reset to auto size"
          >
            Auto
          </button>
        )}

        <button
          type="button"
          onClick={save}
          className="flex shrink-0 items-center gap-1 rounded-lg bg-blue-500 px-3 py-2 text-xs font-semibold text-white hover:bg-blue-600"
        >
          <Check className="h-3 w-3" /> Done
        </button>
      </div>

      {error && <p className="text-xs text-red-500">{error}</p>}
      <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleFile} />
    </div>
  );
}

/** Routes to the correct inline editor based on element type */
function InlineElementEditor({ element, onUpdate, onDone, brand }: InlineEditorProps) {
  if (element.type === "text" || element.type === "static_content" || element.type === "custom") {
    return <InlineTextEdit element={element} onUpdate={onUpdate} onDone={onDone} brand={brand} />;
  }
  if (element.type === "rich_text") {
    const source = element.content?.source as string | undefined;
    const html = element.content?.html as string | undefined;
    const isOldPlaceholder = typeof html === "string" &&
      html.includes("Dear [Client Name]") && html.includes("Thank you for the opportunity");
    if (source === "brand_cover_letter" || isOldPlaceholder) {
      // Pre-fill with brand cover letter content so user can customize it for this proposal
      const initialHtml = brand?.coverLetterHtml || html || "<p>Write your cover letter here.</p>";
      return (
        <div className="w-full space-y-2">
          <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-violet-300/70">
            Cover Letter — editing for this proposal
          </p>
          <InlineRichTextEdit
            element={{ ...element, content: { ...element.content, html: initialHtml } }}
            onUpdate={(upd) => onUpdate({ ...upd, content: { ...(upd.content ?? {}), source: "custom" } })}
            onDone={onDone}
          />
        </div>
      );
    }
    return <InlineRichTextEdit element={element} onUpdate={onUpdate} onDone={onDone} />;
  }
  if (element.type === "badge") {
    return <InlineBadgeEdit element={element} onUpdate={onUpdate} onDone={onDone} />;
  }
  if (element.type === "spacer") {
    return <InlineSpacerEdit element={element} onUpdate={onUpdate} onDone={onDone} />;
  }
  if (element.type === "image" || element.type === "logo") {
    return <InlineImageEdit element={element} onUpdate={onUpdate} onDone={onDone} />;
  }
  // For tables, pricing, signatures — not inline editable; show preview + hint
  return (
    <div className="relative">
      <BuilderElementPreview element={element} />
      <div className="absolute inset-0 flex items-center justify-center rounded-xl bg-white/60 backdrop-blur-[1px]">
        <p className="rounded-full border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-500 shadow-sm">
          Use the inspector panel to edit
        </p>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// SortableElement
// ---------------------------------------------------------------------------

interface SortableElementProps {
  element: BuilderElementDraft;
  isSelected: boolean;
  onSelect: (e: React.MouseEvent) => void;
  onDelete: (e: React.MouseEvent) => void;
  onUpdate: (updates: Partial<BuilderElementDraft>) => void;
  readOnly: boolean;
  proposalContext?: import("./ProposalBuilderCanvas").ProposalCanvasContext;
  pageSettings?: Record<string, unknown>;
  onUpdateLineItem?: (id: string, updates: LineItemUpdate) => void;
}

function SortableElement({
  element,
  isSelected,
  onSelect,
  onDelete,
  onUpdate,
  readOnly,
  proposalContext,
  pageSettings,
  onUpdateLineItem,
}: SortableElementProps) {
  const { data: brandSettings } = useBrandSettings();
  const { data: brandAssets = [] } = useBrandAssets();
  const { data: brandFonts = [] } = useBrandFonts();
  const secondaryLogoAsset = brandAssets.find((a) => a.asset_role === "secondary_logo");
  const addressParts = [
    brandSettings?.address_line1,
    brandSettings?.address_line2,
    brandSettings?.city,
    brandSettings?.state,
    brandSettings?.zip,
  ].filter(Boolean);

  const useCustomColors = brandSettings?.use_custom_colors ?? false;
  const brandPrimaryColor = useCustomColors
    ? ((brandSettings?.color_palette?.primary as string | undefined) ?? null)
    : null;

  const typography = resolveBrandTypography(brandSettings, brandFonts);

  const brandData: BrandPreviewData = {
    companyName:       brandSettings?.company_name      ?? null,
    primaryLogoUrl:    brandSettings?.logo_url          ?? null,
    secondaryLogoUrl:  secondaryLogoAsset?.public_url   ?? null,
    phone:             brandSettings?.phone             ?? null,
    website:           brandSettings?.website           ?? null,
    email:             brandSettings?.email             ?? null,
    address:           addressParts.length > 0 ? addressParts.join(", ") : null,
    tagline:           brandSettings?.tagline           ?? null,
    primaryColor:      brandPrimaryColor,
    textColor:         useCustomColors ? (brandSettings?.text_color ?? null) : null,
    backgroundColor:   useCustomColors ? (brandSettings?.background_color ?? null) : null,
    headingFontFamily: typography.headingFont.fontFamily,
    bodyFontFamily:    typography.bodyFont.fontFamily,
    coverLetterHtml:   brandSettings?.cover_letter_html ?? null,
    signoff:           brandSettings?.signoff           ?? null,
    // Proposal-specific data threaded from the editor
    prospectName:      proposalContext?.prospectName    ?? null,
    prospectAddress:   proposalContext?.prospectAddress ?? null,
    lineItems:         proposalContext?.lineItems,
    subtotal:          proposalContext?.subtotal        ?? null,
    total:             proposalContext?.total           ?? null,
  };

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: `el:${element.id}` });

  const containerRef = useRef<HTMLDivElement>(null);
  const dndRef       = (node: HTMLDivElement | null) => {
    setNodeRef(node);
    (containerRef as React.MutableRefObject<HTMLDivElement | null>).current = node;
  };

  const style                = { transform: CSS.Transform.toString(transform), transition };
  const [hovered, setHovered] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const hoverTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Debounced enter/leave so moving from element → toolbar doesn't flash the toolbar away
  function onHoverEnter() {
    if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);
    setHovered(true);
  }
  function onHoverLeave() {
    hoverTimerRef.current = setTimeout(() => setHovered(false), 80);
  }

  // Exit edit mode when element is deselected
  useEffect(() => {
    if (!isSelected) setIsEditing(false);
  }, [isSelected]);

  // Live resize label state (shows px dimensions while dragging)
  const [resizeLabel, setResizeLabel] = useState<string | null>(null);

  // Page-level visibility rules — must come AFTER all hooks
  const showLogo           = pageSettings?.showLogo !== false;
  const showProspectDetail = pageSettings?.showProspectDetails !== false;
  if (element.type === "logo"            && !showLogo)           return null;
  if (element.type === "project_summary" && !showProspectDetail) return null;

  const canEdit          = !readOnly && isInlineEditable(element.type);
  const showControls     = (hovered || isSelected) && !isDragging && !isEditing;
  const showResizeHandles = isSelected && isMediaElement(element.type) && !readOnly && !isDragging && !isEditing;

  // Resize drag logic — all dimensions in pixels
  function startResize(e: React.MouseEvent, direction: HandlePosition) {
    e.stopPropagation();
    e.preventDefault();

    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return;

    const startX      = e.clientX;
    const startY      = e.clientY;
    const startW      = rect.width;
    const startH      = rect.height;
    // Read stored px values, or derive from current rendered size
    const curWidthPx  = (element.style?.width as number | undefined) ?? Math.round(startW);
    const curHeightPx = (element.style?.imageHeight as number | undefined) ?? Math.round(startH);

    function onMove(mv: MouseEvent) {
      const dx = mv.clientX - startX;
      const dy = mv.clientY - startY;
      let newW = curWidthPx;
      let newH = curHeightPx;
      if (direction.includes("e")) newW = Math.round(Math.max(40, curWidthPx + dx));
      if (direction.includes("w")) newW = Math.round(Math.max(40, curWidthPx - dx));
      if (direction.includes("s")) newH = Math.round(Math.max(40, curHeightPx + dy));
      if (direction.includes("n")) newH = Math.round(Math.max(40, curHeightPx - dy));
      setResizeLabel(`${newW} × ${newH} px`);
      onUpdate({ style: { width: newW, imageHeight: newH } });
    }
    function onUp() {
      setResizeLabel(null);
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
    }
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
  }

  return (
    <div
      ref={dndRef}
      style={style}
      className={cn(
        "group relative transition-all",
        isDragging && "z-50 opacity-30",
        isEditing ? "cursor-text" : canEdit ? "cursor-pointer" : "cursor-default",
      )}
      onMouseEnter={onHoverEnter}
      onMouseLeave={onHoverLeave}
      onClick={(e) => {
        e.stopPropagation();
        // Always select on click
        if (!isSelected) onSelect(e);
        // Text-like elements → enter edit mode immediately on any click
        // Other editable types → enter edit mode only when already selected
        if (canEdit && !isEditing) {
          const isTextLike = (
            element.type === "text" ||
            element.type === "rich_text" ||
            element.type === "static_content" ||
            element.type === "custom"
          );
          if (isTextLike || isSelected) {
            setIsEditing(true);
          }
        }
      }}
    >
      {/* Selection / hover ring — not shown while editing */}
      {(isSelected || hovered) && !isDragging && !isEditing && (
        <div
          className={cn(
            "pointer-events-none absolute inset-0 z-10 rounded-xl transition",
            isSelected
              ? "ring-2 ring-blue-500 ring-offset-2 ring-offset-white"
              : "ring-1 ring-black/15 ring-offset-1 ring-offset-white",
          )}
        />
      )}

      {/* Resize handles — image / logo elements only */}
      {showResizeHandles && (
        <>
          {HANDLE_POSITIONS.map((pos) => (
            <div
              key={pos}
              className={cn(
                "absolute z-30 h-3 w-3 rounded-full border-2 border-white bg-blue-500 shadow-lg",
                handlePositionClass(pos),
              )}
              onMouseDown={(e) => startResize(e, pos)}
            />
          ))}
          {/* Live px dimensions label */}
          {resizeLabel && (
            <div className="pointer-events-none absolute -top-7 left-1/2 z-40 -translate-x-1/2 rounded-full border border-white/20 bg-[#1c1e22]/95 px-2.5 py-0.5 font-mono text-[10px] text-white/80 shadow-lg backdrop-blur-sm">
              {resizeLabel}
            </div>
          )}
          {/* Size dimensions are shown in the toolbar type-label slot — no separate overlay needed */}
        </>
      )}

      {/* Floating toolbar */}
      {showControls && (
        <div
          className="absolute -top-9 left-1/2 z-20 -translate-x-1/2 pointer-events-auto"
          onMouseEnter={onHoverEnter}
          onMouseLeave={onHoverLeave}
        >
          <div className="flex items-center gap-0.5 rounded-full border border-white/25 bg-[#1c1e22]/95 px-2 py-1.5 shadow-xl backdrop-blur-sm">
            {/* Drag handle */}
            <button
              type="button"
              {...attributes}
              {...listeners}
              disabled={readOnly}
              title="Drag to reorder"
              className="cursor-grab touch-none rounded p-0.5 text-white/40 hover:text-white/80 active:cursor-grabbing disabled:cursor-default"
              onClick={(e) => e.stopPropagation()}
            >
              <GripVertical className="h-3 w-3" />
            </button>

            <span className="mx-1 h-3 w-px bg-white/15" />

            {/* Alignment */}
            {ALIGN_OPTIONS.map(({ value, Icon, title }) => {
              const current = (element.style?.alignment as string | undefined) ?? "left";
              return (
                <button
                  key={value}
                  type="button"
                  title={title}
                  disabled={readOnly}
                  onClick={(e) => {
                    e.stopPropagation();
                    const isTextLike = ["text", "rich_text", "static_content", "custom"].includes(element.type);
                    onUpdate({
                      style: {
                        alignment: value,
                        ...(isTextLike ? { textAlign: value } : {}),
                      },
                    });
                  }}
                  className={cn(
                    "rounded p-0.5 transition",
                    current === value
                      ? "text-blue-400"
                      : "text-white/35 hover:text-white/70",
                  )}
                >
                  <Icon className="h-3 w-3" />
                </button>
              );
            })}

            <span className="mx-1 h-3 w-px bg-white/15" />

            {/* Type label — shows px dimensions for image/logo, type name otherwise */}
            <span className="flex items-center gap-1 px-0.5 font-mono text-[9px] uppercase tracking-[0.1em] text-white/40">
              {elementTypeIcon(element.type)}
              {(element.type === "image" || element.type === "logo") && (element.style?.width || element.style?.imageHeight)
                ? `${element.style.width ? `${element.style.width}px` : "auto"} × ${element.style.imageHeight ? `${element.style.imageHeight}px` : "auto"}`
                : element.type}
            </span>

            {/* Edit */}
            {canEdit && (
              <>
                <span className="mx-1 h-3 w-px bg-white/15" />
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); setIsEditing(true); }}
                  title="Edit element"
                  className="rounded p-0.5 text-white/35 hover:text-blue-400"
                >
                  <Pencil className="h-3 w-3" />
                </button>
              </>
            )}

            <span className="mx-1 h-3 w-px bg-white/15" />

            {/* Delete */}
            <button
              type="button"
              onClick={onDelete}
              disabled={readOnly}
              title="Delete element"
              className="rounded p-0.5 text-white/35 hover:text-red-400 disabled:opacity-30"
            >
              <Trash2 className="h-3 w-3" />
            </button>
          </div>
        </div>
      )}

      {/* "Click to edit" hint — only for non-text editable types that need a second click */}
      {isSelected && !isEditing && canEdit && !isDragging &&
        element.type !== "text" && element.type !== "rich_text" &&
        element.type !== "static_content" && element.type !== "custom" && (
        <div className="pointer-events-none absolute -bottom-6 left-1/2 z-10 -translate-x-1/2 whitespace-nowrap rounded-full border border-white/15 bg-[#1c1e22]/80 px-2.5 py-0.5 text-[9px] text-white/40">
          Click to edit
        </div>
      )}

      {/* Content — inline editor or pure preview */}
      <div className={cn("flex px-1 py-2", alignFlexClass(element.style?.alignment))}>
        {isEditing ? (
          <InlineElementEditor
            element={element}
            onUpdate={onUpdate}
            onDone={() => setIsEditing(false)}
            brand={brandData}
          />
        ) : (
          <BuilderElementPreview element={element} brand={brandData} pageSettings={pageSettings} onUpdateLineItem={onUpdateLineItem} />
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// AddElementBar
// ---------------------------------------------------------------------------

const ELEMENT_ADD_OPTIONS: Array<{ type: TemplatePageElementType; label: string }> = [
  { type: "text",            label: "Text" },
  { type: "rich_text",       label: "Rich Text" },
  { type: "image",           label: "Image" },
  { type: "divider",         label: "Divider" },
  { type: "spacer",          label: "Spacer" },
  { type: "badge",           label: "Badge" },
  { type: "company_info",    label: "Company" },
  { type: "project_summary", label: "Summary" },
  { type: "service_table",   label: "Services" },
  { type: "pricing_summary", label: "Pricing" },
  { type: "signature_block", label: "Signature" },
];

function AddElementBar({ onAdd, readOnly }: { onAdd: (type: TemplatePageElementType) => void; readOnly: boolean }) {
  return (
    <div className="mt-4 flex flex-wrap gap-1.5 border-t border-gray-100 pt-4">
      <p className="w-full text-[10px] font-semibold uppercase tracking-[0.14em] text-gray-400">
        Add element
      </p>
      {ELEMENT_ADD_OPTIONS.map((opt) => (
        <button
          key={opt.type}
          type="button"
          disabled={readOnly}
          onClick={(e) => { e.stopPropagation(); onAdd(opt.type); }}
          className="inline-flex items-center gap-1.5 rounded-full border border-gray-200 bg-gray-50 px-2.5 py-1 text-[11px] font-medium text-gray-600 transition hover:border-gray-300 hover:bg-gray-100 hover:text-gray-800 disabled:opacity-40"
        >
          {elementTypeIcon(opt.type)}
          {opt.label}
        </button>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// SortableSection — section is an invisible container; toolbar on hover
// ---------------------------------------------------------------------------

interface SortableSectionProps {
  section: CanvasSectionDraft;
  isSelected: boolean;
  selectedElementId: string | null;
  proposalContext?: import("./ProposalBuilderCanvas").ProposalCanvasContext;
  pageSettings?: Record<string, unknown>;
  onSelectSection: (e: React.MouseEvent) => void;
  onSelectElement: (elementId: string, e: React.MouseEvent) => void;
  onDeleteSection: () => void;
  onDeleteElement: (elementId: string, e: React.MouseEvent) => void;
  onUpdateElement: (elementId: string, updates: Partial<BuilderElementDraft>) => void;
  onAddElement: (type: TemplatePageElementType) => void;
  readOnly: boolean;
  onUpdateLineItem?: (id: string, updates: LineItemUpdate) => void;
}

function SortableSection({
  section,
  isSelected,
  selectedElementId,
  onSelectSection,
  onSelectElement,
  onDeleteSection,
  onDeleteElement,
  onUpdateElement,
  onAddElement,
  readOnly,
  proposalContext,
  pageSettings,
  onUpdateLineItem,
}: SortableSectionProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: `sc:${section.id}` });

  const style = { transform: CSS.Transform.toString(transform), transition };
  const [hovered, setHovered] = useState(false);
  const showControls = (hovered || isSelected) && !isDragging;
  const elementIds   = section.elements.map((e) => `el:${e.id}`);
  const isStack      = section.layoutMode === "stack";

  const rawBg        = section.settings?.backgroundColor;
  const sectionBg    = typeof rawBg === "string" && rawBg ? rawBg : null;
  const bgOpacity    = typeof section.settings?.backgroundOpacity === "number"
    ? section.settings.backgroundOpacity / 100
    : 1;
  const sectionBgStyle: React.CSSProperties = sectionBg
    ? { backgroundColor: hexToRgba(sectionBg, bgOpacity) }
    : {};

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        "group relative transition-all",
        isDragging && "z-50 opacity-30",
      )}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={onSelectSection}
    >
      {/* Selection / hover outline — wraps the section */}
      {(isSelected || hovered) && !isDragging && (
        <div
          className={cn(
            "pointer-events-none absolute inset-0 z-10 rounded-2xl transition",
            isSelected
              ? "ring-2 ring-blue-500/70 ring-offset-4 ring-offset-white"
              : "ring-1 ring-black/10 ring-offset-2 ring-offset-white",
          )}
        />
      )}

      {/* Floating section toolbar — appears at top-center on hover/select */}
      {showControls && (
        <div className="absolute -top-6 left-1/2 z-20 -translate-x-1/2">
          <div className="flex items-center gap-1.5 rounded-full border border-white/25 bg-[#1c1e22]/95 px-2.5 py-1.5 shadow-xl backdrop-blur-sm">
            <button
              type="button"
              {...attributes}
              {...listeners}
              disabled={readOnly}
              title="Drag to reorder section"
              className="cursor-grab touch-none text-white/35 hover:text-white/75 active:cursor-grabbing disabled:cursor-default"
              onClick={(e) => e.stopPropagation()}
            >
              <GripVertical className="h-3.5 w-3.5" />
            </button>

            <span className="h-3 w-px bg-white/15" />

            <span
              className={cn(
                "rounded-full border px-2 py-0.5 text-[9px] uppercase tracking-[0.1em]",
                sectionTypeAccentClass(section.type),
              )}
            >
              {section.type}
            </span>

            <span className="flex items-center gap-1 text-[10px] text-white/40">
              {layoutModeIcon(section.layoutMode)}
            </span>

            <span className="h-3 w-px bg-white/15" />

            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onDeleteSection(); }}
              disabled={readOnly}
              title="Delete section"
              className="rounded p-0.5 text-white/35 transition hover:text-red-400 disabled:opacity-30"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
      )}

      {/* Section body — no header card, just the content area */}
      <div
        className={cn(
          "rounded-2xl px-5 py-5 transition-colors",
          !sectionBg && (isSelected
            ? "bg-blue-50/40"
            : hovered
            ? "bg-black/[0.012]"
            : "bg-transparent"),
        )}
        style={sectionBgStyle}
        onClick={(e) => e.stopPropagation()}
      >
        {section.elements.length === 0 ? (
          <div className="flex min-h-[140px] flex-col items-center justify-center gap-3 rounded-xl border border-dashed border-gray-200 bg-gray-50 px-6 py-8 text-center">
            <Rows3 className="h-5 w-5 text-gray-300" />
            <p className="text-sm text-gray-400">
              No elements yet — select the section and add from the bar below
            </p>
          </div>
        ) : (
          <SortableContext
            items={elementIds}
            strategy={isStack ? verticalListSortingStrategy : rectSortingStrategy}
          >
            <div className={cn("grid gap-6", getGridClass(section.layoutMode))}>
              {section.elements.map((element) => (
                <SortableElement
                  key={element.id}
                  element={element}
                  isSelected={selectedElementId === element.id && isSelected}
                  onSelect={(e) => { e.stopPropagation(); onSelectElement(element.id, e); }}
                  onDelete={(e) => { e.stopPropagation(); onDeleteElement(element.id, e); }}
                  onUpdate={(updates) => onUpdateElement(element.id, updates)}
                  readOnly={readOnly}
                  proposalContext={proposalContext}
                  pageSettings={pageSettings}
                  onUpdateLineItem={onUpdateLineItem}
                />
              ))}
            </div>
          </SortableContext>
        )}

        {/* Add element bar — shown when section is selected */}
        {isSelected && (
          <AddElementBar onAdd={onAddElement} readOnly={readOnly} />
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// AddSectionRow
// ---------------------------------------------------------------------------

const SECTION_QUICK_ADD: Array<{ type: TemplatePageSectionType; label: string }> = [
  { type: "hero",     label: "Hero" },
  { type: "content",  label: "Content" },
  { type: "media",    label: "Media" },
  { type: "services", label: "Services" },
  { type: "summary",  label: "Summary" },
  { type: "footer",   label: "Footer" },
  { type: "custom",   label: "Custom" },
];

function AddSectionRow({
  onAddSection,
  readOnly,
}: {
  onAddSection: (type: TemplatePageSectionType) => void;
  readOnly: boolean;
}) {
  const [open, setOpen] = useState(false);

  return (
    <div className="flex flex-col items-center gap-3 py-6">
      {open ? (
        <div className="flex w-full flex-wrap justify-center gap-2 rounded-2xl border border-dashed border-gray-200 bg-gray-50 px-4 py-5">
          <p className="w-full text-center text-[10px] font-semibold uppercase tracking-[0.14em] text-gray-400">
            Choose section type
          </p>
          {SECTION_QUICK_ADD.map((s) => (
            <button
              key={s.type}
              type="button"
              disabled={readOnly}
              onClick={() => { onAddSection(s.type); setOpen(false); }}
              className="rounded-xl border border-gray-200 bg-white px-3 py-2 text-xs font-medium text-gray-700 shadow-sm transition hover:border-gray-300 hover:text-gray-900 disabled:opacity-40"
            >
              {s.label}
            </button>
          ))}
          <button
            type="button"
            onClick={() => setOpen(false)}
            className="mt-1 w-full text-center text-[11px] text-gray-400 hover:text-gray-600"
          >
            Cancel
          </button>
        </div>
      ) : (
        <button
          type="button"
          disabled={readOnly}
          onClick={() => setOpen(true)}
          className="inline-flex items-center gap-2 rounded-full border border-dashed border-gray-300 bg-gray-50 px-5 py-2 text-xs font-medium text-gray-500 transition hover:border-gray-400 hover:bg-gray-100 hover:text-gray-700 disabled:opacity-40"
        >
          <Plus className="h-3.5 w-3.5" />
          Add section
        </button>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Empty page state
// ---------------------------------------------------------------------------

function EmptyPageStage({
  pageTitle,
  pageType,
  onAddSection,
  readOnly,
}: {
  pageTitle: string;
  pageType: string;
  onAddSection: (type: TemplatePageSectionType) => void;
  readOnly: boolean;
}) {
  return (
    <div className="flex min-h-[480px] flex-col items-center justify-center gap-6 rounded-2xl border border-dashed border-gray-200 bg-gray-50 px-6 py-12 text-center">
      <div className="flex h-14 w-14 items-center justify-center rounded-2xl border border-gray-200 bg-white shadow-sm">
        <FileText className="h-5 w-5 text-gray-400" />
      </div>

      <div>
        <p className="text-base font-semibold text-gray-800">{pageTitle || "Untitled Page"}</p>
        <p className="mt-1 text-xs font-semibold uppercase tracking-[0.14em] text-gray-400">{pageType}</p>
        <p className="mt-3 text-sm leading-6 text-gray-500">
          No sections yet. Pick a section type to start building.
        </p>
      </div>

      <div className="flex flex-wrap justify-center gap-2">
        {SECTION_QUICK_ADD.map((s) => (
          <button
            key={s.type}
            type="button"
            disabled={readOnly}
            onClick={() => onAddSection(s.type)}
            className="inline-flex items-center gap-1.5 rounded-xl border border-gray-200 bg-white px-3.5 py-2 text-xs font-medium text-gray-700 shadow-sm transition hover:border-gray-300 hover:text-gray-900 disabled:opacity-40"
          >
            <Plus className="h-3 w-3" />
            {s.label}
          </button>
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// NothingSelected
// ---------------------------------------------------------------------------

function NothingSelected() {
  return (
    <div className="flex min-h-[580px] flex-col items-center justify-center gap-4 px-6 text-center">
      <div className="flex h-14 w-14 items-center justify-center rounded-2xl border border-white/10 bg-white/[0.04]">
        <FileText className="h-5 w-5 text-white/35" />
      </div>
      <div>
        <p className="text-sm font-medium text-white/60">No page selected</p>
        <p className="mt-1 text-xs text-white/35">
          Choose a page from the left panel to start editing.
        </p>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// CanvasPageStageProps
// ---------------------------------------------------------------------------

export interface CanvasPageStageProps {
  page: ProposalDocumentLayoutPage | null;
  selection: CanvasSelection;
  onSelectSection: (sectionId: string) => void;
  onSelectElement: (sectionId: string, elementId: string) => void;
  onClearSelection: () => void;
  onDeleteSection: (sectionId: string) => void;
  onDeleteElement: (sectionId: string, elementId: string) => void;
  onUpdateElement: (sectionId: string, elementId: string, updates: Partial<BuilderElementDraft>) => void;
  onAddSection: (type: TemplatePageSectionType) => void;
  onAddElement: (sectionId: string, type: TemplatePageElementType) => void;
  readOnly?: boolean;
  className?: string;
  proposalContext?: import("./ProposalBuilderCanvas").ProposalCanvasContext;
  onUpdateLineItem?: (id: string, updates: LineItemUpdate) => void;
}

// ---------------------------------------------------------------------------
// CanvasPageStage
// ---------------------------------------------------------------------------

export function CanvasPageStage({
  page,
  selection,
  onSelectSection,
  onSelectElement,
  onClearSelection,
  onDeleteSection,
  onDeleteElement,
  onUpdateElement,
  onAddSection,
  onAddElement,
  readOnly = false,
  className,
  proposalContext,
  onUpdateLineItem,
}: CanvasPageStageProps) {
  const { data: canvasBrandSettings } = useBrandSettings();
  const { data: canvasBrandFonts = [] } = useBrandFonts();

  const canvasTypography = resolveBrandTypography(canvasBrandSettings, canvasBrandFonts);
  const canvasFontFaceCSS = buildFontFaceCSS(canvasTypography);
  const canvasUseCustomColors = canvasBrandSettings?.use_custom_colors ?? false;
  const canvasBgColor = canvasUseCustomColors ? (canvasBrandSettings?.background_color ?? null) : null;

  if (!page) {
    return (
      <main className={cn("flex w-full flex-col overflow-y-auto bg-[#111315]", className)}>
        <NothingSelected />
      </main>
    );
  }

  // ── Sections ───────────────────────────────────────────────────────────────
  const sections          = normalizeSections(page);
  const sectionIds        = sections.map((s) => `sc:${s.id}`);
  const selectedSectionId =
    selection?.kind === "section" || selection?.kind === "element"
      ? selection.sectionId : null;
  const selectedElementId =
    selection?.kind === "element" ? selection.elementId : null;

  function pageTypeLabel(type?: string) {
    switch (type) {
      case "cover":        return "Cover Page";
      case "cover_letter": return "Cover Letter";
      case "line_items":   return "Services & Pricing";
      case "static_page":  return "Static Page";
      case "custom":       return "Custom Page";
      case "closing":      return "Closing Page";
      case "signature":    return "Signature Page";
      default:             return "Page";
    }
  }

  // Custom background color from page settings → brand background → white
  const settings = page.settings as Record<string, unknown> | null | undefined;
  const pageBackground = (typeof settings?.backgroundColor === "string" && settings.backgroundColor)
    ? settings.backgroundColor
    : (canvasBgColor ?? "#ffffff");

  return (
    <main
      className={cn("flex w-full flex-col overflow-y-auto bg-[#111315]", className)}
      onClick={onClearSelection}
    >
      {/* @font-face for any uploaded brand fonts — injected once at canvas level */}
      {canvasFontFaceCSS && <style>{canvasFontFaceCSS}</style>}
      <div className="mx-auto w-full max-w-[860px] px-4 py-10">
        {/* ── Page paper — background only, font applied to content area below ── */}
        <div
          className="relative min-h-[960px] overflow-hidden rounded-[32px] border border-black/10 shadow-[0_32px_80px_rgba(15,23,42,0.14),0_8px_24px_rgba(15,23,42,0.06)]"
          style={{ backgroundColor: pageBackground }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Page label — canvas UI chrome, always Inter */}
          <div className="border-b border-black/[0.06] px-10 py-5" style={{ fontFamily: "Inter, system-ui, sans-serif" }}>
            <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-gray-400">
              {pageTypeLabel(page.type)}
            </p>
            <p className="mt-0.5 text-lg font-semibold text-gray-800">
              {page.title || "Untitled Page"}
            </p>
          </div>

          {/* Sections content — brand body font scoped here, never bleeds into chrome */}
          <div
            className="px-10 py-8"
            style={{
              fontFamily: canvasTypography.bodyFont.fontFamily,
            }}
          >
            {/* ── Page-type settings header (rendered above sections) ── */}
            {(() => {
              if (page.type === "cover") {
                const headline    = typeof settings?.headline    === "string" ? settings.headline    : "";
                const subheadline = typeof settings?.subheadline === "string" ? settings.subheadline : "";
                if (!headline && !subheadline) return null;
                return (
                  <div className="mb-6 space-y-1.5">
                    {headline    && <p className="text-2xl font-bold text-gray-800">{headline}</p>}
                    {subheadline && <p className="text-base text-gray-500">{subheadline}</p>}
                  </div>
                );
              }
              if (page.type === "cover_letter" || page.type === "line_items" || page.type === "custom") {
                const showTitle = page.type !== "custom" || settings?.showPageTitle !== false;
                const title     = typeof settings?.title     === "string" ? settings.title     : "";
                const introText = typeof settings?.introText === "string" ? settings.introText : "";
                if ((!showTitle || !title) && !introText) return null;
                return (
                  <div className="mb-6 space-y-1.5">
                    {showTitle && title && <p className="text-2xl font-bold text-gray-800">{title}</p>}
                    {introText && <p className="text-base leading-relaxed text-gray-500">{introText}</p>}
                  </div>
                );
              }
              return null;
            })()}

            {sections.length === 0 ? (
              <EmptyPageStage
                pageTitle={page.title || "Untitled Page"}
                pageType={pageTypeLabel(page.type)}
                onAddSection={onAddSection}
                readOnly={readOnly}
              />
            ) : (
              <SortableContext items={sectionIds} strategy={verticalListSortingStrategy}>
                <div className="space-y-2">
                  {sections.map((section, index) => (
                    <div key={section.id}>
                      {index > 0 && <div className="my-4 border-t border-black/5" />}
                      <SortableSection
                        section={section}
                        isSelected={selectedSectionId === section.id}
                        selectedElementId={
                          selectedSectionId === section.id ? selectedElementId : null
                        }
                        onSelectSection={(e) => {
                          e.stopPropagation();
                          onSelectSection(section.id);
                        }}
                        onSelectElement={(elementId, e) => {
                          e.stopPropagation();
                          onSelectElement(section.id, elementId);
                        }}
                        onDeleteSection={() => onDeleteSection(section.id)}
                        onDeleteElement={(elementId, e) => {
                          e.stopPropagation();
                          onDeleteElement(section.id, elementId);
                        }}
                        onUpdateElement={(elementId, updates) =>
                          onUpdateElement(section.id, elementId, updates)
                        }
                        onAddElement={(type) => onAddElement(section.id, type)}
                        readOnly={readOnly}
                        proposalContext={proposalContext}
                        pageSettings={settings as Record<string, unknown> | undefined}
                        onUpdateLineItem={onUpdateLineItem}
                      />
                    </div>
                  ))}
                </div>
              </SortableContext>
            )}

            {sections.length > 0 && (
              <AddSectionRow onAddSection={onAddSection} readOnly={readOnly} />
            )}

            {/* ── Cover letter closing block (settings-driven) ── */}
            {page.type === "cover_letter" && (() => {
              const showSignature = settings?.showCompanySignature !== false;
              const showDate      = settings?.showDate             !== false;
              const closingText   = typeof settings?.closingText === "string" ? settings.closingText : "";
              if (!closingText && !showSignature && !showDate) return null;
              return (
                <div className="mt-6 space-y-1.5 border-t border-black/8 pt-5">
                  {closingText && <p className="text-sm leading-relaxed text-gray-500">{closingText}</p>}
                  {showSignature && canvasBrandSettings?.company_name && (
                    <p className="text-sm font-medium text-gray-700">{canvasBrandSettings.company_name}</p>
                  )}
                  {showDate && (
                    <p className="text-xs text-gray-400">
                      {new Date().toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}
                    </p>
                  )}
                </div>
              );
            })()}

            {/* ── Closing page structured blocks (settings-driven) ── */}
            {page.type === "closing" && (() => {
              const showSummary  = settings?.showSummaryBlock  !== false;
              const showCta      = settings?.showCallToAction  !== false;
              const showContact  = settings?.showContactBlock  !== false;
              if (!showSummary && !showCta && !showContact) return null;
              const summaryHeading = typeof settings?.summaryHeading === "string" ? settings.summaryHeading : "Next Steps";
              const closingMessage = typeof settings?.closingMessage  === "string" ? settings.closingMessage : "";
              const ctaLabel       = typeof settings?.ctaLabel        === "string" ? settings.ctaLabel       : "Ready to get started";
              const ctaText        = typeof settings?.ctaText         === "string" ? settings.ctaText        : "";
              return (
                <div className="mt-8 space-y-4 border-t border-black/8 pt-6" style={{ fontFamily: canvasTypography.bodyFont.fontFamily }}>
                  {showSummary && (
                    <div className="rounded-xl border border-black/10 bg-black/[0.02] px-5 py-4">
                      <p className="text-sm font-semibold text-gray-700">{summaryHeading}</p>
                      {closingMessage && <p className="mt-2 text-sm leading-relaxed text-gray-500">{closingMessage}</p>}
                    </div>
                  )}
                  {showCta && (
                    <div className="rounded-xl bg-gray-800 px-5 py-4 text-white">
                      <p className="font-semibold">{ctaLabel}</p>
                      {ctaText && <p className="mt-1 text-sm text-white/70">{ctaText}</p>}
                    </div>
                  )}
                  {showContact && canvasBrandSettings && (
                    <div className="space-y-1 text-sm text-gray-500">
                      {canvasBrandSettings.company_name && <p className="font-medium text-gray-700">{canvasBrandSettings.company_name}</p>}
                      {canvasBrandSettings.phone   && <p>{canvasBrandSettings.phone}</p>}
                      {canvasBrandSettings.email   && <p>{canvasBrandSettings.email}</p>}
                      {canvasBrandSettings.website && <p>{canvasBrandSettings.website}</p>}
                    </div>
                  )}
                </div>
              );
            })()}
          </div>
        </div>
      </div>
    </main>
  );
}
